import ApplicationController from "./application_controller"
import {
  chunkTextForSpeech,
  mainReadableText,
  primeSpeechVoices,
  speakParts,
  stopSpeech
} from "../lib/lexiqamus/speech_synthesis"

const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY = "lexiqamusTextScale"
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS = [0.875, 1, 1.125, 1.25, 1.5]
const LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER = 1
const LEXIQAMUS_ACCESSIBILITY_DEFAULT_SPEECH_LOCALE = "en-US"

// Returns true when the browser supports Web Speech API for voice input
function isSpeechRecognitionSupported() {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
}

// Creates and configures a browser SpeechRecognition instance with callbacks
function createSpeechRecognition({ lang, onResult, onEnd, onError }) {
  const speechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!speechRecognitionConstructor) return null

  const recognition = new speechRecognitionConstructor()
  recognition.lang = lang
  recognition.interimResults = false
  recognition.maxAlternatives = 1
  recognition.onresult = (event) => {
    const transcript = event.results[0]?.[0]?.transcript?.trim()
    if (transcript) onResult(transcript)
  }
  recognition.onerror = (event) => onError?.(event)
  recognition.onend = () => onEnd?.()
  return recognition
}

// Rounds text scale to the nearest allowed step to prevent invalid values
function nearestAllowedTextScale(value) {
  const parsedTextScale = parseFloat(value)
  if (!Number.isFinite(parsedTextScale)) return LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
  return LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.find(
    (multiplier) => Math.abs(multiplier - parsedTextScale) < 0.001
  ) || LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
}

// LexiQamus accessibility toolbar: large-print text scaling, voice input, and text-to-speech.
// Text scale applies a CSS variable (--lq-text-scale) used throughout LexiQamus layout.
// Voice input uses browser Web Speech API to transcribe speech into the search bar.
// TTS speaks search results or main content aloud via browser speechSynthesis.
export default class extends ApplicationController {
  static targets = ["status", "micButton", "decreaseTextButton", "resetTextButton", "increaseTextButton"]

  static values = {
    locale: String,
    listening: String,
    voiceUnsupported: String,
    voiceError: String
  }

  // Initializes text scale from localStorage and hides voice button if unsupported
  connect() {
    this.recognition = null
    this.listeningActive = false
    this.textScale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    primeSpeechVoices()
    this.restoreTextScale()
    this.updateVoiceInputButtonVisibility()
  }

  // Cleanup: stops voice recognition and cancels any active speech playback
  disconnect() {
    this.stopListening()
    stopSpeech()
  }

  // Hides microphone button when Web Speech API is not available in the browser
  updateVoiceInputButtonVisibility() {
    if (!this.hasMicButtonTarget) return
    this.micButtonTarget.hidden = !isSpeechRecognitionSupported()
  }

  // Loads saved text scale from localStorage on page load
  restoreTextScale() {
    let scale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    try {
      scale = nearestAllowedTextScale(window.localStorage.getItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    this.setLargePrintTextScale(scale, scale !== LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
  }

  decreaseTextSize() {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    if (currentMultiplierIndex > 0) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex - 1])
    }
  }

  increaseTextSize() {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    const lastMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
    if (currentMultiplierIndex >= 0 && currentMultiplierIndex < lastMultiplierIndex) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex + 1])
    }
  }

  resetTextSize() {
    this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
  }

  // Applies text scale via CSS variable, persists to localStorage, and triggers layout recalc
  setLargePrintTextScale(scale, notifyLayout = true) {
    this.textScale = nearestAllowedTextScale(scale)
    document.documentElement.style.setProperty("--lq-text-scale", String(this.textScale))
    this.updateTextSizeToolbarButtonStates()

    try {
      window.localStorage.setItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY, String(this.textScale))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    // Notify layout engines that viewport-relative sizing may have changed
    if (notifyLayout) window.dispatchEvent(new Event("resize"))
  }

  // Updates disabled state and aria-pressed for text size toolbar buttons
  updateTextSizeToolbarButtonStates() {
    if (this.hasDecreaseTextButtonTarget) {
      this.decreaseTextButtonTarget.disabled = this.textScale <= LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[0]
    }
    if (this.hasIncreaseTextButtonTarget) {
      const largestTextScaleMultiplier = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[
        LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
      ]
      this.increaseTextButtonTarget.disabled = this.textScale >= largestTextScaleMultiplier
    }
    if (this.hasResetTextButtonTarget) {
      this.resetTextButtonTarget.setAttribute(
        "aria-pressed",
        this.textScale === LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER ? "true" : "false"
      )
    }
  }

  // Toggles voice input: starts listening if idle, stops if already active
  toggleVoiceInput() {
    if (this.listeningActive) this.stopListening()
    else this.startListening()
  }

  // Starts Web Speech API voice recognition and shows "Listening..." status
  startListening() {
    if (!isSpeechRecognitionSupported()) {
      this.setToolbarStatusMessage(this.voiceUnsupportedValue)
      return
    }

    this.recognition = createSpeechRecognition({
      lang: this.speechLocale(),
      onResult: (transcript) => this.insertVoiceTranscriptIntoSearchInput(transcript),
      onError: () => this.setToolbarStatusMessage(this.voiceErrorValue),
      onEnd: () => this.resetVoiceInputListeningUI()
    })
    if (!this.recognition) return

    this.listeningActive = true
    if (this.hasMicButtonTarget) this.micButtonTarget.setAttribute("aria-pressed", "true")
    this.setToolbarStatusMessage(this.listeningValue)
    this.recognition.start()
  }

  // Stops active voice recognition session
  stopListening() {
    if (this.recognition) {
      try { this.recognition.stop() } catch (_e) { /* ignore */ }
      this.recognition = null
    }
    this.resetVoiceInputListeningUI()
  }

  // Clears listening state and resets microphone button aria-pressed
  resetVoiceInputListeningUI() {
    this.listeningActive = false
    if (this.hasMicButtonTarget) this.micButtonTarget.setAttribute("aria-pressed", "false")
    this.setToolbarStatusMessage("")
  }

  // Inserts voice transcript into LexiQamus search input and triggers input event
  insertVoiceTranscriptIntoSearchInput(transcript) {
    const input = document.querySelector('[data-lexiqamus-search-bar-target="input"]')
    if (!input) return

    input.value = input.value ? `${input.value} ${transcript}` : transcript
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.focus()
  }

  // Stops both voice input listening and any active TTS playback
  stopSpeechAndVoiceInput() {
    this.stopListening()
    stopSpeech()
    this.setToolbarStatusMessage("")
  }

  // Speaks the main LexiQamus content area aloud via text-to-speech
  speakMainContentAloud() {
    speakParts(chunkTextForSpeech(mainReadableText()), { lang: this.speechLocale() })
  }

  // Speaks a single search result aloud from its data-speak-text attribute
  speakSearchResultAloud(event) {
    event.stopPropagation()
    speakParts(chunkTextForSpeech(event.currentTarget.dataset.speakText || ""), { lang: this.speechLocale() })
  }

  speechLocale() {
    return this.localeValue || LEXIQAMUS_ACCESSIBILITY_DEFAULT_SPEECH_LOCALE
  }

  // Updates toolbar status message (e.g., "Listening...", error messages)
  setToolbarStatusMessage(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message || ""
  }
}
